import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { CircularProgress, Grid, MenuItem } from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import { memo, useCallback, useEffect, useId, useRef, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { zodResolver } from '@hookform/resolvers/zod';
import { isAxiosError } from 'axios';
import { ButtonContainer, EditButton, ExtendedTextField, FileInputUploader, GridContainer, ProfileFormHeader, ProfileFormWrapper, ProfilePicture, ProfilePictureWrapper, ProfileTitle, } from './styled';
import { profileScheme } from './validation';
import { ResetButton } from '../../styled';
import ImageSizeAlert from '@/entities/notifications/ImageSizeAlert';
import { UploadButton, ResetPictureButton } from '@/pages/SignUp/model/SignUpForm/styled';
import UserService from '@/shared/api/services/UserService';
import { WritingPenIcon } from '@/shared/assets/icons';
import { usePictureUploader } from '@/shared/lib/hooks';
import { userStore } from '@/shared/model/user';
import CountryPicker from '@/shared/ui/CountryPicker';
import SubmitButton from '@/shared/ui/SubmitButton';
import TransitionedModal from '@/shared/ui/TransitionedModal';
import { avatarPlaceholder } from '@/shared/assets/images';
import { ExplicitContentAlert } from '@/entities/notifications/ExplicitContentAlert';
import useMobileResize from '@/pages/BecomeHost/lib/hooks/useMobileResize';
import { handleSentryInfo } from '@/shared/utils/handleSentryInfo';
const ProfileForm = observer(({ onEnableClose, onDisableClose }) => {
    const { user, age } = userStore;
    const [loading, setLoading] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const { isMobileSize } = useMobileResize();
    const { handleSubmit, reset, control, formState: { errors, isValid }, } = useForm({
        defaultValues: user,
        resolver: zodResolver(profileScheme),
    });
    const [openAlert, setOpenAlert] = useState(false);
    const [openExplicitContentAlert, setOpenExplicitContentAlert] = useState(false);
    const handleClose = useCallback(() => {
        setOpenAlert(false);
        setOpenExplicitContentAlert(false);
    }, []);
    const { key, fileData, loading: pictureLoading, handleFileChange, setFileData, } = usePictureUploader(() => {
        setOpenAlert(true);
    }, () => {
        setOpenExplicitContentAlert(true);
    }, {
        defaultFileData: {
            format: user.profilePictureFormat,
            data: user.profilePicture,
        },
    });
    const loaderId = useId();
    const pictureLoaderId = useId();
    const picturePlaceholder = fileData?.data ? _jsx(ProfilePicture, { src: fileData?.data }) : '+';
    useEffect(() => {
        if (!isEditMode) {
            setFileData({
                format: user.profilePictureFormat,
                data: user.profilePicture,
            });
        }
    }, [isEditMode, setFileData, user.profilePicture, user.profilePictureFormat]);
    const inputPictureRef = useRef(null);
    const handleUploadClick = () => {
        inputPictureRef.current?.click();
    };
    const onSubmit = useCallback(async (data) => {
        handleSentryInfo('profile', `Edit profile (save): ${JSON.stringify(data)}`);
        if (!isValid)
            return;
        setLoading(true);
        onDisableClose();
        const pictureData = fileData?.data ?? null;
        const pictureFormat = fileData?.format ?? null;
        const response = await UserService.updateUserDetails({
            ...data,
            userId: user.id,
            data: pictureData ?? '',
            format: pictureFormat ?? '',
        });
        if (response && !isAxiosError(response)) {
            userStore.user = {
                ...user,
                ...data,
                profilePicture: pictureData ?? '',
                profilePictureFormat: pictureFormat ?? '',
            };
        }
        setIsEditMode(false);
        setLoading(false);
        onEnableClose();
    }, [fileData?.data, fileData?.format, isValid, onDisableClose, onEnableClose, user]);
    const handleReset = () => {
        reset();
        setIsEditMode(false);
    };
    const handleEdit = () => {
        setIsEditMode(true);
    };
    const renderButtons = () => {
        if (!isEditMode)
            return null;
        if (isMobileSize) {
            return (_jsxs(GridContainer, { children: [_jsx(ButtonContainer, { children: _jsx(SubmitButton, { loading: loading, "aria-describedby": loading ? loaderId : undefined, "aria-busy": loading, height: 48, loaderId: loaderId, children: "Save changes" }) }), _jsx(ButtonContainer, { children: _jsx(ResetButton, { type: "reset", onClick: handleReset, children: "Cancel" }) })] }));
        }
        return (_jsxs(Grid, { container: true, item: true, sx: { alignSelf: "center" }, spacing: 10, xs: 10, children: [_jsx(Grid, { item: true, xs: 6, display: "flex", justifyContent: "center", alignItems: "center", children: _jsx(SubmitButton, { width: 160, height: 48, loaderId: loaderId, loading: loading, "aria-describedby": loading ? loaderId : undefined, "aria-busy": loading, children: "Save changes" }) }), _jsx(Grid, { item: true, xs: 6, display: "flex", justifyContent: "center", alignItems: "center", children: _jsx(ResetButton, { color: "secondary", type: "reset", onClick: handleReset, children: "Cancel" }) })] }));
    };
    return (_jsxs(_Fragment, { children: [_jsx(TransitionedModal, { open: openAlert, children: _jsx(ImageSizeAlert, { onClose: handleClose }) }), _jsx(TransitionedModal, { open: openExplicitContentAlert, children: _jsx(ExplicitContentAlert, { onClose: handleClose }) }), _jsxs(ProfileFormWrapper, { onSubmit: handleSubmit(onSubmit), "aria-describedby": loading ? loaderId : undefined, "aria-busy": loading, children: [_jsxs(ProfileFormHeader, { children: [_jsxs(ProfilePictureWrapper, { children: [isEditMode ? (_jsx(UploadButton, { onClick: isEditMode && !loading ? handleUploadClick : undefined, type: 'button', "aria-busy": pictureLoading, "aria-describedby": pictureLoading ? pictureLoaderId : undefined, children: pictureLoading ? (_jsx(CircularProgress, { id: pictureLoaderId, color: 'primary', size: 20 })) : (picturePlaceholder) })) : (_jsx(ProfilePicture, { src: user.profilePicture ? user.profilePicture : avatarPlaceholder, alt: user.userName })), isEditMode && fileData?.data ? (_jsx(ResetPictureButton, { onClick: handleUploadClick, children: _jsx(WritingPenIcon, {}) })) : null, _jsx(FileInputUploader, { type: 'file', ref: inputPictureRef, onChange: handleFileChange, accept: '.png,.jpg,.jpeg', "aria-hidden": true, "aria-busy": pictureLoading }, key)] }), _jsx(ProfileTitle, { children: "Profile Photo" }), isEditMode ? null : (_jsxs(EditButton, { color: 'secondary', type: 'button', onClick: handleEdit, children: [_jsx(WritingPenIcon, {}), " Edit"] }))] }), _jsxs(Grid, { container: true, spacing: 15, children: [_jsx(Grid, { item: true, xs: 12, children: _jsx(Controller, { name: 'userName', control: control, render: ({ field: { ref, ...field } }) => (_jsx(ExtendedTextField, { inputProps: { readOnly: !isEditMode || loading }, ...field, fullWidth: true, inputRef: ref, disabled: !isEditMode || loading, label: 'Name', error: !!errors.userName, helperText: errors.userName?.message, value: isEditMode ? field.value : `${field.value}, ${age}` })) }) }), _jsx(Grid, { item: true, xs: 12, children: _jsx(Controller, { name: 'country', control: control, render: ({ field: { ref, onChange, ...field } }) => (_jsx(CountryPicker, { inputRef: ref, fullWidth: true, onAutoCompleteChange: onChange, onChange: onChange, disabled: !isEditMode || loading, label: 'Country', InputProps: {
                                            readOnly: !isEditMode || loading,
                                        }, TextField: ExtendedTextField, ...field, defaultValue: { flag: '', name: { common: user.country } } })) }) }), _jsx(Grid, { item: true, xs: 12, children: _jsx(Controller, { name: 'gender', control: control, render: ({ field: { ref, ...field } }) => (_jsxs(ExtendedTextField, { ...field, fullWidth: true, inputRef: ref, disabled: !isEditMode || loading, select: true, label: 'Gender', inputProps: { readOnly: !isEditMode || loading }, error: !!errors.gender, helperText: errors.gender?.message, children: [_jsx(MenuItem, { value: 'Male', children: "Male" }), _jsx(MenuItem, { value: 'Female', children: "Female" }), _jsx(MenuItem, { value: 'Other', children: "Other" })] })) }) }), renderButtons()] })] })] }));
});
export default memo(ProfileForm);
